import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceService, userService, searchClientsAsync } from '../services/api';
import AsyncSearchableSelect from '../components/common/AsyncSearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import CreateInvoiceModal from '../components/invoices/CreateInvoiceModal';
import SendInvoiceModal from '../components/invoices/SendInvoiceModal';
import AddPaymentModal from '../components/invoices/AddPaymentModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  DollarSign, FileText, CreditCard, Plus, Send, Eye,
  AlertTriangle, CheckCircle, Clock, Ban, ShieldCheck,
  Landmark, ChevronDown, Download, Search, ExternalLink, Users,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'partial', label: 'Partial' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' },
];

const Billing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = hasPermission(user, 'approve_invoice');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'invoices';
  const [mainTab, setMainTab] = useState(initialTab);

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sendModalInvoice, setSendModalInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [batchMessage, setBatchMessage] = useState(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilterId, setClientFilterId] = useState('');
  const [clientFilterLabel, setClientFilterLabel] = useState('');
  const [attorneyFilter, setAttorneyFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attorneys, setAttorneys] = useState([]);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [showBatchMenu, setShowBatchMenu] = useState(false);

  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [balanceSearch, setBalanceSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAttorneys = async () => {
      try {
        const response = await userService.getUsers();
        setAttorneys(Array.isArray(response.data) ? response.data : []);
      } catch (err) { console.error('Error fetching attorneys:', err); }
    };
    fetchAttorneys();
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      setInvoicesLoading(true);
      setError(null);
      const params = { ...pagination };
      if (statusFilter) params.status = statusFilter;
      if (clientFilterId) params.client_id = clientFilterId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await invoiceService.getInvoices(params);
      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (err) { console.error('Error fetching invoices:', err); setInvoices([]); setError('Failed to load invoices. Please try refreshing the page.'); }
    finally { setInvoicesLoading(false); }
  }, [statusFilter, clientFilterId, startDate, endDate, pagination]);

  const fetchStats = useCallback(async () => {
    try { const response = await invoiceService.getInvoiceStats(); setStats(response.data); }
    catch (error) { console.error('Error fetching invoice stats:', error); }
  }, []);

  const fetchBalances = useCallback(async () => {
    try {
      setBalancesLoading(true);
      setError(null);
      const response = await invoiceService.getClientBalances();
      setBalances(Array.isArray(response.data) ? response.data : []);
    } catch (err) { console.error('Error fetching balances:', err); setBalances([]); setError('Failed to load balances. Please try refreshing the page.'); }
    finally { setBalancesLoading(false); }
  }, []);

  useEffect(() => {
    if (mainTab === 'invoices') { fetchInvoices(); fetchStats(); }
    else { fetchBalances(); }
  }, [mainTab, fetchInvoices, fetchStats, fetchBalances]);

  const handleMainTabChange = (tab) => { setMainTab(tab); setSearchParams({ tab }); };

  const handleClientSelect = (value, option) => {
    if (value) { setClientFilterId(value); setClientFilterLabel(option?.label || ''); }
    else { setClientFilterId(''); setClientFilterLabel(''); }
    setPagination(p => ({ ...p, skip: 0 }));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleRowClick = (index, e) => {
    if (e.target.closest('a') || e.target.closest('button') || e.target.type === 'checkbox') return;
    const id = invoices[index].id;
    if (e.shiftKey && lastClickedIndex !== null) {
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) next.add(invoices[i].id);
        return next;
      });
    } else if (e.ctrlKey || e.metaKey) { toggleSelect(id); }
    else { setSelectedIds(new Set([id])); }
    setLastClickedIndex(index);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === invoices.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(invoices.map(i => i.id)));
  };

  const selectedInvoices = invoices.filter(i => selectedIds.has(i.id));
  const selectedDrafts = selectedInvoices.filter(i => i.status === 'draft');
  const selectedPendingApproval = selectedInvoices.filter(i => i.status === 'pending_approval');
  const selectedApproved = selectedInvoices.filter(i => i.status === 'approved');

  const runBatchAction = async (action, label) => {
    setBatchProcessing(true); setBatchMessage(null);
    try {
      const response = await action();
      const result = response.data;
      setBatchMessage({ variant: result.failed?.length ? 'warning' : 'success', text: result.message });
      setSelectedIds(new Set()); fetchInvoices(); fetchStats();
    } catch (error) { setBatchMessage({ variant: 'error', text: `Failed to ${label}` }); }
    finally { setBatchProcessing(false); }
  };

  const handleSendInvoice = async (email) => {
    if (!sendModalInvoice) return;
    try { await invoiceService.sendInvoice(sendModalInvoice.id, email); fetchInvoices(); setSendModalInvoice(null); }
    catch (error) { console.error('Error sending invoice:', error); throw error; }
  };

  const handleVoidInvoice = async (invoiceId) => {
    setConfirmDialog({
      isOpen: true, title: 'Void Invoice',
      message: 'Are you sure you want to void this invoice? This action cannot be undone.',
      confirmText: 'Void Invoice', variant: 'danger',
      onConfirm: async () => {
        try { await invoiceService.voidInvoice(invoiceId); fetchInvoices(); }
        catch (error) { console.error('Error voiding:', error); }
      }
    });
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await invoiceService.downloadInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoice_number || invoice.id}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error('Error downloading PDF:', error); }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'pending_approval': return <Clock className="w-4 h-4" />;
      case 'approved': return <ShieldCheck className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'partial': return <Clock className="w-4 h-4" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4" />;
      case 'void': return <Ban className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const filteredBalances = balances.filter(b =>
    !balanceSearch || b.client_name.toLowerCase().includes(balanceSearch.toLowerCase())
  );
  const totalOutstanding = filteredBalances.reduce((sum, b) => sum + b.total_balance, 0);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <DollarSign className="w-8 h-8 mr-3 text-yellow-600" />Billing
            </h1>
            <p className="text-gray-600 mt-1">Manage invoices and client balances</p>
          </div>
          {mainTab === 'invoices' && (
            <button onClick={() => setShowCreateModal(true)} className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center">
              <Plus className="w-4 h-4 mr-2" />New Invoice
            </button>
          )}
        </div>
        <div className="border-t border-gray-200 -mx-6 -mb-6 px-6">
          <div className="flex space-x-0">
            {[
              { id: 'invoices', label: 'Invoices', icon: FileText },
              { id: 'balances', label: 'Balances', icon: CreditCard },
            ].map(tab => (
              <button key={tab.id} onClick={() => handleMainTabChange(tab.id)}
                className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
                  mainTab === tab.id
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
          <button onClick={() => { setError(null); if (mainTab === 'invoices') { fetchInvoices(); fetchStats(); } else { fetchBalances(); } }}
            className="text-sm text-red-600 hover:text-red-800 underline ml-4">Retry</button>
        </div>
      )}

      {/* INVOICES TAB */}
      {mainTab === 'invoices' && (<>
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><DollarSign className="w-8 h-8 text-yellow-600 mr-3" /><div><p className="text-sm text-gray-600">Total Outstanding</p><p className="text-xl font-bold text-yellow-600">{invoiceService.formatCurrency(stats.total_outstanding)}</p></div></div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><AlertTriangle className="w-8 h-8 text-red-600 mr-3" /><div><p className="text-sm text-gray-600">Overdue</p><p className="text-xl font-bold text-red-600">{invoiceService.formatCurrency(stats.overdue_amount)}</p><p className="text-xs text-gray-500">{stats.overdue_count} invoices</p></div></div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><CheckCircle className="w-8 h-8 text-green-600 mr-3" /><div><p className="text-sm text-gray-600">Paid This Month</p><p className="text-xl font-bold text-green-600">{invoiceService.formatCurrency(stats.paid_amount)}</p></div></div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><FileText className="w-8 h-8 text-gray-600 mr-3" /><div><p className="text-sm text-gray-600">Total Invoices</p><p className="text-xl font-bold text-gray-600">{stats.total_invoices}</p><p className="text-xs text-gray-500">{stats.draft_count} drafts</p></div></div></div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[180px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, skip: 0 })); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500">
                {STATUS_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
            </div>
            <div className="min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
              <AsyncSearchableSelect value={clientFilterId} onChange={handleClientSelect} onSearch={searchClientsAsync}
                placeholder="Search clients..." selectedLabel={clientFilterLabel} emptyOption={{ value: '', label: 'All Clients' }} />
            </div>
            <div className="min-w-[180px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Attorney</label>
              <select value={attorneyFilter} onChange={(e) => { setAttorneyFilter(e.target.value); setPagination(p => ({ ...p, skip: 0 })); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500">
                <option value="">All Attorneys</option>
                {attorneys.map(a => (<option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPagination(p => ({ ...p, skip: 0 })); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPagination(p => ({ ...p, skip: 0 })); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500" />
            </div>
            {(statusFilter || clientFilterId || attorneyFilter || startDate || endDate) && (
              <button onClick={() => { setStatusFilter(''); setClientFilterId(''); setClientFilterLabel(''); setAttorneyFilter(''); setStartDate(''); setEndDate(''); setPagination(p => ({ ...p, skip: 0 })); }}
                className="text-sm text-gray-500 hover:text-gray-700 underline pb-2">Clear filters</button>
            )}
          </div>
        </div>

        {/* Batch Actions */}
        {selectedIds.size > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-yellow-800">{selectedIds.size} invoice{selectedIds.size > 1 ? 's' : ''} selected</span>
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-yellow-600 hover:text-yellow-800 underline">Clear selection</button>
            </div>
            <div className="flex items-center space-x-3">
              {batchProcessing && <span className="text-sm text-gray-500">Processing...</span>}
              <div className="relative">
                <button onClick={() => setShowBatchMenu(!showBatchMenu)} onBlur={() => setTimeout(() => setShowBatchMenu(false), 200)}
                  disabled={batchProcessing} className="bg-yellow-500 text-white px-4 py-1.5 rounded-lg hover:bg-yellow-600 flex items-center text-sm disabled:opacity-50">
                  Actions <ChevronDown className="w-4 h-4 ml-2" />
                </button>
                {showBatchMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {selectedDrafts.length > 0 && (
                      <button onClick={() => { setShowBatchMenu(false); setConfirmDialog({ isOpen: true, title: 'Send for Approval', message: `Submit ${selectedDrafts.length} draft invoice${selectedDrafts.length > 1 ? 's' : ''} for approval?`, onConfirm: () => runBatchAction(() => invoiceService.batchSubmitForApproval(selectedDrafts.map(i => i.id)), 'submit for approval'), variant: 'warning', confirmText: 'Submit All' }); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        <Send className="w-4 h-4 mr-3 text-yellow-500" />Send for Approval ({selectedDrafts.length})
                      </button>
                    )}
                    {canApprove && (selectedDrafts.length > 0 || selectedPendingApproval.length > 0) && (
                      <button onClick={() => { setShowBatchMenu(false); const toApprove = selectedInvoices.filter(i => i.status === 'draft' || i.status === 'pending_approval'); setConfirmDialog({ isOpen: true, title: 'Approve Invoices', message: `Approve ${toApprove.length} invoice${toApprove.length > 1 ? 's' : ''}?`, onConfirm: () => runBatchAction(() => invoiceService.batchApproveInvoices(toApprove.map(i => i.id)), 'approve invoices'), variant: 'warning', confirmText: 'Approve All' }); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        <ShieldCheck className="w-4 h-4 mr-3 text-green-500" />Approve ({selectedDrafts.length + selectedPendingApproval.length})
                      </button>
                    )}
                    {selectedApproved.length > 0 && (<>
                      <div className="border-t border-gray-100 my-1" />
                      <button onClick={() => { setShowBatchMenu(false); setConfirmDialog({ isOpen: true, title: 'Apply Trust', message: `Apply trust funds to ${selectedApproved.length} approved invoice${selectedApproved.length > 1 ? 's' : ''}?`, onConfirm: () => runBatchAction(() => invoiceService.batchApplyTrust(selectedApproved.map(i => i.id)), 'apply trust'), variant: 'warning', confirmText: 'Apply Trust' }); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        <Landmark className="w-4 h-4 mr-3 text-blue-500" />Apply Trust ({selectedApproved.length})
                      </button>
                      <button onClick={() => { setShowBatchMenu(false); setConfirmDialog({ isOpen: true, title: 'Send Invoices', message: `Send ${selectedApproved.length} approved invoice${selectedApproved.length > 1 ? 's' : ''} to clients?`, onConfirm: () => runBatchAction(() => invoiceService.batchSendInvoices(selectedApproved.map(i => i.id)), 'send invoices'), variant: 'warning', confirmText: 'Send All' }); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        <Send className="w-4 h-4 mr-3 text-yellow-500" />Send ({selectedApproved.length})
                      </button>
                    </>)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {batchMessage && (
          <div className={`rounded-lg px-4 py-3 text-sm ${batchMessage.variant === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : batchMessage.variant === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {batchMessage.text}
            <button onClick={() => setBatchMessage(null)} className="ml-3 underline text-xs">Dismiss</button>
          </div>
        )}

        {/* Invoice Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {invoicesLoading && invoices.length === 0 ? (
            <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div></div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center"><FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">No invoices found</p></div>
          ) : (<>
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="flex items-center gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="w-8 flex-shrink-0"><input type="checkbox" checked={invoices.length > 0 && selectedIds.size === invoices.length} onChange={toggleSelectAll} className="h-4 w-4 text-yellow-600 rounded border-gray-300" /></div>
                <div className="grid grid-cols-12 gap-4 flex-1">
                  <div className="col-span-2">Invoice #</div><div className="col-span-2">Client</div><div className="col-span-2">Case</div>
                  <div className="col-span-1">Status</div><div className="col-span-1">Date</div><div className="col-span-1">Due</div>
                  <div className="col-span-2">Amount</div><div className="col-span-1">Actions</div>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {invoices.map((invoice, index) => (
                <div key={invoice.id} className={`px-6 py-4 cursor-pointer select-none hover:bg-gray-50 ${selectedIds.has(invoice.id) ? 'bg-yellow-50' : ''}`} onClick={(e) => handleRowClick(index, e)}>
                  <div className="flex items-center gap-4">
                    <div className="w-8 flex-shrink-0"><input type="checkbox" checked={selectedIds.has(invoice.id)} onChange={() => toggleSelect(invoice.id)} className="h-4 w-4 text-yellow-600 rounded border-gray-300" /></div>
                    <div className="grid grid-cols-12 gap-4 items-center flex-1">
                      <div className="col-span-2"><Link to={`/invoices/${invoice.id}`} className="text-yellow-600 hover:text-yellow-700 font-medium">{invoice.invoice_number}</Link></div>
                      <div className="col-span-2"><p className="text-sm text-gray-900">{invoice.client_name}</p></div>
                      <div className="col-span-2"><p className="text-sm text-gray-900 truncate">{invoice.case_title}</p></div>
                      <div className="col-span-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${invoiceService.getStatusColor(invoice.status)}`}>
                          <span className="mr-1">{getStatusIcon(invoice.status)}</span>{invoiceService.getStatusLabel(invoice.status)}
                        </span>
                        {invoice.is_overdue && (<div className="text-xs text-red-600 mt-1">{invoiceService.getDaysOverdue(invoice)} days overdue</div>)}
                      </div>
                      <div className="col-span-1"><p className="text-sm text-gray-600">{invoiceService.formatDate(invoice.invoice_date)}</p></div>
                      <div className="col-span-1"><p className={`text-sm ${invoice.is_overdue ? 'text-red-600' : 'text-gray-600'}`}>{invoiceService.formatDate(invoice.due_date)}</p></div>
                      <div className="col-span-2">
                        <p className="text-sm font-medium text-gray-900">{invoiceService.formatCurrency(invoice.total_amount)}</p>
                        {parseFloat(invoice.paid_amount) > 0 && (<p className="text-xs text-green-600">Paid: {invoiceService.formatCurrency(invoice.paid_amount)}</p>)}
                        {invoiceService.calculateBalance(invoice) > 0 && (<p className="text-xs text-gray-500">Balance: {invoiceService.formatCurrency(invoiceService.calculateBalance(invoice))}</p>)}
                      </div>
                      <div className="col-span-1">
                        <div className="flex space-x-2">
                          <Link to={`/invoices/${invoice.id}`} className="text-gray-400 hover:text-gray-600" title="View Details"><Eye className="w-4 h-4" /></Link>
                          <button onClick={() => handleDownloadPDF(invoice)} className="text-gray-400 hover:text-gray-600" title="Download PDF"><Download className="w-4 h-4" /></button>
                          {invoice.status === 'draft' && (
                            <button onClick={async () => { try { await invoiceService.submitForApproval(invoice.id); fetchInvoices(); } catch (e) { /* noop */ } }}
                              className="text-yellow-500 hover:text-yellow-700" title="Send for Approval"><Send className="w-4 h-4" /></button>
                          )}
                          {invoice.status === 'pending_approval' && canApprove && (
                            <button onClick={async () => { try { await invoiceService.approveInvoice(invoice.id); fetchInvoices(); } catch (e) { /* noop */ } }}
                              className="text-green-500 hover:text-green-700" title="Approve"><ShieldCheck className="w-4 h-4" /></button>
                          )}
                          {invoice.status === 'approved' && (<>
                            <button onClick={async () => { try { await invoiceService.applyTrust(invoice.id); fetchInvoices(); } catch (e) { /* noop */ } }}
                              className="text-blue-500 hover:text-blue-700" title="Apply Trust"><Landmark className="w-4 h-4" /></button>
                            <button onClick={() => setSendModalInvoice(invoice)} className="text-yellow-500 hover:text-yellow-700" title="Send Invoice"><Send className="w-4 h-4" /></button>
                          </>)}
                          {(invoice.status === 'sent' || invoice.status === 'partial') && (<>
                            <button onClick={() => setPaymentInvoice(invoice)} className="text-green-400 hover:text-green-600" title="Record Payment"><DollarSign className="w-4 h-4" /></button>
                            <button onClick={() => handleVoidInvoice(invoice.id)} className="text-red-400 hover:text-red-600" title="Void Invoice"><Ban className="w-4 h-4" /></button>
                          </>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {invoices.length === pagination.limit && (
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <button onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))} disabled={pagination.skip === 0} className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">Previous</button>
                  <span className="text-sm text-gray-600">Showing {pagination.skip + 1} - {pagination.skip + invoices.length}</span>
                  <button onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))} className="text-sm text-gray-600 hover:text-gray-800">Next</button>
                </div>
              </div>
            )}
          </>)}
        </div>
      </>)}

      {/* BALANCES TAB */}
      {mainTab === 'balances' && (<>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><DollarSign className="w-8 h-8 text-yellow-600 mr-3" /><div><p className="text-sm text-gray-600">Total Outstanding</p><p className="text-xl font-bold text-yellow-600">{invoiceService.formatCurrency(totalOutstanding)}</p></div></div></div>
          <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><Users className="w-8 h-8 text-blue-600 mr-3" /><div><p className="text-sm text-gray-600">Clients with Balance</p><p className="text-xl font-bold text-blue-600">{filteredBalances.length}</p></div></div></div>
          <div className="bg-white rounded-lg shadow p-4"><div className="flex items-center"><AlertTriangle className="w-8 h-8 text-red-600 mr-3" /><div><p className="text-sm text-gray-600">Avg Balance</p><p className="text-xl font-bold text-red-600">{invoiceService.formatCurrency(filteredBalances.length > 0 ? totalOutstanding / filteredBalances.length : 0)}</p></div></div></div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search clients..." value={balanceSearch} onChange={(e) => setBalanceSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {balancesLoading ? (
            <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div></div>
          ) : filteredBalances.length === 0 ? (
            <div className="p-8 text-center"><CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" /><p className="text-gray-600">{balanceSearch ? 'No clients match your search' : 'No outstanding balances'}</p></div>
          ) : (<>
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="col-span-3">Client</div><div className="col-span-2">Email</div><div className="col-span-1 text-right">Invoices</div>
                <div className="col-span-2 text-right">Total Invoiced</div><div className="col-span-2 text-right">Paid</div><div className="col-span-2 text-right">Balance Due</div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredBalances.map((balance) => (
                <div key={balance.client_id} className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/billing/clients/${balance.client_id}/transactions`)}>
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <div className="flex items-center"><p className="text-sm font-medium text-gray-900">{balance.client_name}</p><ExternalLink className="w-3 h-3 ml-2 text-gray-400" /></div>
                      {balance.last_invoice_date && (<p className="text-xs text-gray-500 mt-0.5">Last invoice: {invoiceService.formatDate(balance.last_invoice_date)}</p>)}
                    </div>
                    <div className="col-span-2"><p className="text-sm text-gray-500 truncate">{balance.client_email || '-'}</p></div>
                    <div className="col-span-1 text-right"><span className="text-sm text-gray-900">{balance.invoice_count}</span></div>
                    <div className="col-span-2 text-right"><p className="text-sm text-gray-900">{invoiceService.formatCurrency(balance.total_invoiced)}</p></div>
                    <div className="col-span-2 text-right"><p className="text-sm text-green-600">{invoiceService.formatCurrency(balance.total_paid)}</p></div>
                    <div className="col-span-2 text-right"><p className="text-sm font-semibold text-red-600">{invoiceService.formatCurrency(balance.total_balance)}</p></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-3"><p className="text-sm font-bold text-gray-900">Total ({filteredBalances.length} clients)</p></div>
                <div className="col-span-2"></div>
                <div className="col-span-1 text-right"><span className="text-sm font-bold text-gray-900">{filteredBalances.reduce((sum, b) => sum + b.invoice_count, 0)}</span></div>
                <div className="col-span-2 text-right"><p className="text-sm font-bold text-gray-900">{invoiceService.formatCurrency(filteredBalances.reduce((sum, b) => sum + b.total_invoiced, 0))}</p></div>
                <div className="col-span-2 text-right"><p className="text-sm font-bold text-green-600">{invoiceService.formatCurrency(filteredBalances.reduce((sum, b) => sum + b.total_paid, 0))}</p></div>
                <div className="col-span-2 text-right"><p className="text-sm font-bold text-red-600">{invoiceService.formatCurrency(totalOutstanding)}</p></div>
              </div>
            </div>
          </>)}
        </div>
      </>)}

      {/* Modals */}
      {showCreateModal && (<CreateInvoiceModal onClose={() => setShowCreateModal(false)} onSuccess={() => { setShowCreateModal(false); setStatusFilter('draft'); fetchInvoices(); fetchStats(); setBatchMessage({ variant: 'success', text: 'Invoice created successfully and saved as draft.' }); }} />)}
      {sendModalInvoice && (<SendInvoiceModal invoice={sendModalInvoice} onClose={() => setSendModalInvoice(null)} onSuccess={handleSendInvoice} />)}
      {paymentInvoice && (<AddPaymentModal invoice={paymentInvoice} onClose={() => setPaymentInvoice(null)} onSuccess={() => { setPaymentInvoice(null); fetchInvoices(); fetchStats(); }} />)}
      <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })} onConfirm={confirmDialog.onConfirm} title={confirmDialog.title} message={confirmDialog.message} variant={confirmDialog.variant || 'danger'} confirmText={confirmDialog.confirmText || 'Confirm'} />
    </div>
  );
};

export default Billing;
